import { Box, FormControl, FormLabel, useToast } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import gql from 'graphql-tag';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { TimeSelect } from '~components/powerSchedules/TimeSelect';
import { isPropertyPending } from '~components/ui/FormLabelPendingIndicator';
import { fromError } from '~utils/errors';
import { HOUR_REGEX } from '~utils/timeBlocks';
import {
  DisplayBurnInRefreshTime_DisplayFragment,
  useUpdateBurnInRefreshTimeMutation,
} from './__generated__/DisplayBurnInRefreshTime.graphql';

interface Props {
  display: DisplayBurnInRefreshTime_DisplayFragment;
  isDisabled?: boolean;
  onPendingChange: (pending: boolean) => void;
}

const getInitialTime = (display: DisplayBurnInRefreshTime_DisplayFragment): string => {
  if (isPropertyPending(display.burnInRefreshSettings)) {
    return display.burnInRefreshSettings?.desired?.time ?? '--:--';
  }
  return display.burnInRefreshSettings?.reported?.time ?? '--:--';
};

export function DisplayBurnInRefreshTime({ display, isDisabled, onPendingChange }: Props) {
  const { verifyUserPermissions } = useAuth();
  const hasPermission = verifyUserPermissions([Permission.DisplaySettingsUpdate]);
  const [startTime, setStartTime] = useState<string>(getInitialTime(display));
  const { t } = useTranslation();
  const toast = useToast();
  const [updateBurnInRefreshTime] = useUpdateBurnInRefreshTimeMutation();

  useEffect(() => {
    onPendingChange(isPropertyPending(display.burnInRefreshSettings));
    setStartTime(getInitialTime(display));
  }, [display, onPendingChange]);

  const handleUpdateTime = useCallback(
    async (time: string) => {
      if (!HOUR_REGEX.test(time)) return;
      try {
        await updateBurnInRefreshTime({
          variables: {
            input: {
              displayIds: [display.id],
              time,
            },
          },
        });
      } catch (err) {
        toast({
          status: 'error',
          title: t('burnInRefreshErr'),
          description: fromError(err, 'UpdateBurnInRefresh'),
        });
      }
    },
    [display, toast, updateBurnInRefreshTime, t],
  );

  const updateTimeValue = (_property: 'start' | 'end', value: string) => {
    if (!HOUR_REGEX.test(value)) return;
    setStartTime(value);
    handleUpdateTime(value);
  };

  return (
    <FormControl>
      <FormLabel>{t('startTime')}</FormLabel>
      <Box maxWidth="80px">
        <TimeSelect
          value={startTime}
          onChange={updateTimeValue}
          property="start"
          disabled={!hasPermission || isDisabled}
        />
      </Box>
    </FormControl>
  );
}

DisplayBurnInRefreshTime.graphql = {
  fragments: {
    DisplayBurnInRefreshTime_display: gql`
      fragment DisplayBurnInRefreshTime_display on Display {
        id
        burnInRefreshSettings {
          reported {
            time
          }
          desired {
            time
          }
        }
      }
    `,
  },
  mutations: {
    UpdateBurnInRefreshTime: gql`
      mutation updateBurnInRefreshTime($input: DisplayBulkUpdateBurnInRefreshInput!) {
        displayBulkUpdateBurnInRefresh(input: $input) {
          displays {
            id
            ...DisplayBurnInRefreshTime_display
          }
        }
      }
    `,
  },
};
